import React, { useEffect, useState } from "react";
import { IFork } from "../../types/fork.type";
import { Currency } from "../../types/currency.type";
import dayjs from "dayjs";
import { api } from "../../api/api";
import { DatePicker, Input, message, Space, Table, Typography } from "antd";
import { roundToThreeDecimals } from "../../utils/roundToThreeDecimals";
import { useParams } from "react-router-dom";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Search } = Input;

export default function Forks() {
  const { id, name } = useParams()
  const [forks, setForks] = useState<IFork[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [total, setTotal] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dateRange, setDateRange] = useState<
    [dayjs.Dayjs, dayjs.Dayjs] | undefined
  >([dayjs().startOf("month"), dayjs().endOf("month")]);

  const handleRangeChange = (dates: any) => {
    if (dates) {
      setDateRange([dates[0], dates[1]]);
    } else {
      setDateRange(undefined);
    }
  };

  const fetchForks = async () => {
    setLoading(true);
    try {
      const response = await api.get("/fork", {
        params: {
          page,
          limit: pageSize,
          startDate: dateRange ? dateRange[0].toISOString() : undefined,
          endDate: dateRange ? dateRange[1].toISOString() : undefined,
          search: searchTerm,
          orderId: id,
        },
      });

      setForks(response.data.forks);
      setTotal(response.data.total);
    } catch (error: any) {
      console.error("Error fetching forks", error);
      message.error(error.response.data.error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchForks();
  }, [page, pageSize, dateRange, searchTerm]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const columns = [
    {
      title: "Дата",
      dataIndex: "date",
      key: "date",
    },
    {
      title: "Чек",
      dataIndex: "receipt",
      key: "receipt",
    },
    {
      title: "Валюта",
      dataIndex: "currency",
      key: "currency",
    },
    {
      title: "Сумма",
      dataIndex: "amount",
      key: "amount",
      render: (amount: number) => roundToThreeDecimals(amount),
    },
    {
      title: "Курс",
      dataIndex: "exchangeRate",
      key: "exchangeRate",
    },
    {
      title: "Цена до в USD",
      key: "priceBefore",
      render: (_: any, record: IFork) =>
        roundToThreeDecimals(record.priceBefore.USD),
    },
    {
      title: "Цена до в TJS",
      key: "priceBefore",
      render: (_: any, record: IFork) =>
        roundToThreeDecimals(record.priceBefore.TJS),
    },
    {
      title: "Цена после в USD",
      key: "priceAfter",
      render: (_: any, record: IFork) =>
        roundToThreeDecimals(record.priceAfter.USD),
    },
    {
      title: "Цена после в TJS",
      key: "priceAfter",
      render: (_: any, record: IFork) =>
        roundToThreeDecimals(record.priceAfter.TJS),
    },
    {
      title: "Касса",
      dataIndex: "cashboxName",
      key: "cashboxName",
    },
    {
      title: "Описание",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Создал",
      dataIndex: "ownerId",
      key: "ownerId",
      render: (ownerId: { username: string }) => ownerId?.username || "",
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title>Откаты {name}</Title>

      <Space style={{ marginBottom: 16, marginTop: 16 }} wrap>
        <Search placeholder="Поиск" onSearch={handleSearch} allowClear />
        <RangePicker
          onChange={handleRangeChange}
          placeholder={["Начало", "Конец"]}
          format="DD.MM.YY"
          value={dateRange}
        />
      </Space>

      <Table
        columns={columns}
        dataSource={forks}
        rowKey={(record) => record._id}
        pagination={{
          current: page,
          total: total,
          pageSize: pageSize,
          onChange: (page, size) => {
            setPage(page);
            if (size !== pageSize) setPageSize(size!);
          },
          showSizeChanger: true,
        }}
        scroll={{ x: "max-content" }}
        loading={loading}
      />
    </div>
  );
}
